import { useCallback, useRef } from 'react';
import { clsx } from 'clsx';
import { usePopover } from '@react-aria/overlays';
import { useInteractOutside } from '@react-aria/interactions';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { Box, Icon, Image, Paragraph } from '@alto-avios/alto-ui';

import { Image as ImageInterface } from '../services/content/models/ModalData';

import styles from './Dropdown.module.scss';

export interface DropdownItem {
  id: string;
  image: ImageInterface;
  label: string;
  buttonId?: string;
}

interface MenuSelectorProps {
  isForm?: boolean;
  hidePickedLabel?: boolean;
  onChange: (id: string) => void;
  options: DropdownItem[];
  value: string;
  accessibilityId?: string;
}

const DropdownItem = ({
  id,
  label,
  image,
  onChange,
  showLabel,
  isForm,
  icon,
  isOption,
  buttonId,
}: DropdownItem & Pick<MenuSelectorProps, 'onChange' | 'isForm'> & { icon?: string; isOption?: boolean; showLabel: boolean; id?: string }) => {
  const handleClick = useCallback(() => {
    onChange(id!);
  }, [onChange, id]);

  return (
    <button
      type="button"
      className={clsx(styles.menu, isForm && styles.isFormMenu, isOption && styles.option)}
      onClick={handleClick}
      id={buttonId}
      data-testid="dropdown-button"
    >
      <Box alignItems="center" gap="2xs">
        <Image borderRadius="4xs" height={showLabel ? 32 : 40} src={image.url} width={showLabel ? 32 : 40} />
        {showLabel && <Paragraph size="sm">{label}</Paragraph>}
      </Box>
      {icon && <Icon iconName={icon} padding="fixedWidth" iconSize={showLabel ? '1x' : '0.75x'} />}
    </button>
  );
};

export const Dropdown = ({ isForm, onChange, options, value, hidePickedLabel, accessibilityId }: MenuSelectorProps) => {
  const selectedItem = options.find((option) => option.id === value)!;
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const state = useOverlayTriggerState({
    defaultOpen: false,
  });

  const handleChange = (id: string) => {
    state.close();
    onChange(id);
  };

  const { popoverProps } = usePopover(
    {
      placement: 'bottom',
      triggerRef,
      popoverRef,
      isNonModal: true,
    },
    state
  );

  useInteractOutside({
    ref: triggerRef,
    onInteractOutside: () => {
      state.close();
    },
  });

  return (
    <div ref={triggerRef} className={clsx(styles.dropdownMenu, !hidePickedLabel && styles.withMinWidth)}>
      <DropdownItem
        {...selectedItem}
        onChange={() => state.toggle()}
        isForm={isForm}
        icon={isForm ? 'angles-up-down' : state.isOpen ? 'chevron-up' : 'chevron-down'}
        showLabel={!hidePickedLabel}
        buttonId={accessibilityId}
      />
      {state.isOpen && (
        <div ref={popoverRef} {...popoverProps} className={styles.options}>
          <Box backgroundColor="base" borderColour={isForm ? 'secondary' : undefined} borderRadius="2xs" flexDirection="column" padding="5xs">
            {options.map((item) => (
              <DropdownItem
                {...item}
                onChange={handleChange}
                isForm={isForm}
                icon={item.id === value ? 'check' : undefined}
                isOption
                showLabel
                key={item.id}
              />
            ))}
          </Box>
        </div>
      )}
    </div>
  );
};
